import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ManageDealInvitesEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import FilterPanelNew from 'components/FilterPanelNew';

import TextField from 'primitives/TextField';

import debounce from 'utils/debounce';

import InvitesTable from './InvitesTable';

const FILTER_PARAM = 'type';

const FILTER_OPTIONS = [
  { label: 'All invited', value: ManageDealInvitesEnumType.Invited },
  { label: 'Opened', value: ManageDealInvitesEnumType.Opened },
  { label: 'Clicked through', value: ManageDealInvitesEnumType.Viewed },
  { label: 'Bounced', value: ManageDealInvitesEnumType.Bounced },
  { label: 'Passed', value: ManageDealInvitesEnumType.Passed },
];

function readFilterFromUrl(rawValue: string | null): string {
  const isKnownFilter = FILTER_OPTIONS.some(option => option.value === rawValue);
  return isKnownFilter && rawValue ? rawValue : ManageDealInvitesEnumType.Invited;
}

const ManageInvites: FC = () => {
  const [searchParams] = useSearchParams();
  const activeFilter = readFilterFromUrl(searchParams.get(FILTER_PARAM));
  const [searchText, setSearchText] = useState('');

  return (
    <>
      <div className="sm:flex sm:items-center sm:justify-between mb-3">
        <div className="max-w-xl">
          <h3 className="text-lg font-semibold leading-6 text-gray-700">Manage your invites</h3>
          <p className="mt-1 text-md text-gray-500">View details about your past invites.</p>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        {/* The URL param drives the query, so FilterPanelNew's own refetch is not needed. */}
        <FilterPanelNew
          filters={[{ type: FILTER_PARAM, label: 'Show', options: FILTER_OPTIONS }]}
          activeFilters={{ [FILTER_PARAM]: activeFilter }}
          refetch={() => undefined}
          showSelectedFilters={false}
          panelAnchor="bottom start"
        />
        <div className="w-full sm:w-72">
          <div className="relative">
            {/* TextField's leadingIcon only adds left padding, so the icon is drawn here, above the input. */}
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 z-10 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <TextField
              fullWidth
              leadingIcon
              placeholder="Search by name or email"
              onChange={debounce(e => setSearchText(e.target.value.trim()), 400)}
            />
          </div>
        </div>
      </div>

      {activeFilter === ManageDealInvitesEnumType.Opened && (
        <p className="mb-4 text-sm text-gray-500">
          Opens are approximate: some email apps load images automatically, so an open does not
          always mean the investor read the email. &ldquo;Clicked through&rdquo; means they followed
          the link to the deal.
        </p>
      )}

      <InvitesTable
        key={`${activeFilter}-${searchText}`}
        type={activeFilter as ManageDealInvitesEnumType}
        searchText={searchText}
      />
    </>
  );
};

export default ManageInvites;
